import { createRef, HTMLAttributes, PureComponent } from "react"
import Spline from "@/lib/spline"
import { clamp } from "@/utils/math"
import { vec2 } from "gl-matrix"

export interface CurveAdjustmentPoint {
	x: number
	y: number
}

interface CurveAdjustmentRect {
	x: number
	y: number
	width: number
	height: number
}

function isPointInRect(rect: CurveAdjustmentRect, point: CurveAdjustmentPoint) {
	return (
		point.x >= rect.x &&
		point.y >= rect.y &&
		point.x <= rect.x + rect.width &&
		point.y <= rect.y + rect.height
	)
}

export interface CurveEditorProps {
	// Name of the curves adjutment
	name: string

	// Accent color while drawing
	color: string

	// Optional theme-aware grid color
	gridColor?: string

	// This only sets initial set of points. Updating this doesn't update the component as it holds internal state
	points: CurveAdjustmentPoint[]

	// Whether to enable events or not.
	disabled: boolean

	// Callbacks
	onStartUpdating: (name: string) => void
	onEndUpdating: (name: string) => void
	onUpdate: (name: string, points: CurveAdjustmentPoint[]) => void
	onKnobPositionUpdate: (position: CurveAdjustmentPoint | null) => void | null
}

export class CurveEditor extends PureComponent<
	CurveEditorProps & HTMLAttributes<HTMLCanvasElement>
> {
	/* Drawing options and data */

	protected drawingOptions: { [name: string]: any }

	/* State */

	/// Currently selected knob index
	protected selectedKnobIndex: number

	/// All points on the graph normalised
	protected points: CurveAdjustmentPoint[]

	/* Management */

	// Reference to the canvas element to be filled by React
	protected canvasRef = createRef<HTMLCanvasElement>()

	// Is currently dragging or not
	protected isDragging: boolean = false

	// Screen scale for High DPI drawing
	protected screenScale: number = window.devicePixelRatio || 1

	// Unsafe canvas context
	protected unsafeCtx?: CanvasRenderingContext2D

	// Raw canvas bounding rect
	protected boundingRect = new DOMRect()

	protected resizeObserver?: ResizeObserver

	// Canvas context to be used for drawing
	protected get ctx(): CanvasRenderingContext2D {
		return this.unsafeCtx as CanvasRenderingContext2D
	}

	// Raw size of canvas (without multiplication by the scale)
	protected size: { width: number; height: number } = {
		width: 0,
		height: 0,
	}

	// Safe rect for canvas drawing
	protected safeRect: CurveAdjustmentRect = {
		x: 0,
		y: 0,
		width: 0,
		height: 0,
	}

	constructor(props: CurveEditorProps) {
		super(props)

		this.selectedKnobIndex = -1
		this.points = props.points || [
			{ x: 0.0, y: 0.0 },
			{ x: 1.0, y: 1.0 },
		]

		this.drawingOptions = {
			// Grid
			gridCount: 4,
			gridThickness: 1,
			gridColor: "#333",

			// Knob
			knobSize: 6,
			knobLineThickness: 4,
			knobDraggingArea: 12,
			knobColor: props.color,

			// Spline
			splineThickness: 2,
			splineColor: props.color,
			splineInterpolationFactor: 2,
		}
	}

	componentDidMount() {
		this.calculateBoundaries()
	}

	componentDidUpdate(prevProps: CurveEditorProps) {
		if (
			prevProps.color !== this.props.color ||
			prevProps.gridColor !== this.props.gridColor ||
			prevProps.disabled !== this.props.disabled
		) {
			this.draw()
		}
	}

	/* Public methods */

	render() {
		return (
			<canvas
				onPointerDown={this.onDrag}
				onPointerUp={this.onDrag}
				onPointerMove={this.onDrag}
				onClick={this.onClick}
				onDoubleClick={this.onClick}
				ref={this.canvasRef}
				className={this.props.className}
				style={this.props.style}
				draggable="false"
			/>
		)
	}

	update(points: CurveAdjustmentPoint[]) {
		this.points = points
		this.selectedKnobIndex = Math.min(this.selectedKnobIndex, this.points.length - 1)
		this.draw()
	}

	reset() {
		this.points = [
			{ x: 0.0, y: 0.0 },
			{ x: 1.0, y: 1.0 },
		]
		this.deselect()
	}

	deselect() {
		this.selectedKnobIndex = -1
		this.draw()
	}

	removeSelectedKnobAndUpdate() {
		if (this.points.length <= 2) {
			return
		}

		this.props.onStartUpdating(this.props.name)

		this.removeSelectedKnob()

		this.props.onUpdate(this.props.name, this.points)
		this.props.onEndUpdating(this.props.name)

		this.selectedKnobIndex = Math.min(this.selectedKnobIndex, this.points.length - 1)

		const point = this.points[this.selectedKnobIndex]

		if (point) {
			this.props.onKnobPositionUpdate(point)
		}

		this.draw()
	}

	/* Sizing */

	protected calculateBoundaries = () => {
		const canvas = this.canvasRef.current

		if (!canvas) {
			return
		}

		this.size.width = canvas.offsetWidth
		this.size.height = canvas.offsetHeight

		this.safeRect.x = this.drawingOptions.knobSize
		this.safeRect.y = this.drawingOptions.knobSize
		this.safeRect.width = this.size.width - this.drawingOptions.knobSize * 2
		this.safeRect.height = this.size.height - this.drawingOptions.knobSize * 2

		canvas.width = this.size.width * this.screenScale
		canvas.height = this.size.height * this.screenScale

		this.unsafeCtx = canvas.getContext("2d") as CanvasRenderingContext2D
		this.ctx.scale(this.screenScale, this.screenScale)

		this.boundingRect = canvas.getBoundingClientRect()

		const resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0]

			if (
				!entry ||
				entry.contentRect.width === 0 ||
				entry.contentRect.height === 0 ||
				(entry.contentRect.width === this.size.width &&
					entry.contentRect.height === this.size.height)
			) {
				return
			}

			this.calculateBoundaries()
			resizeObserver.disconnect()
		})

		resizeObserver.observe(canvas)

		this.resizeObserver = resizeObserver

		this.draw()
	}

	/* Events handling */

	// Handles tapping on the control:
	// - Selecting existing knobs
	// - Creating new ones
	protected onClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
		if (this.props.disabled === true) {
			return
		}

		event.preventDefault()
		event.stopPropagation()

		if (this.isDragging === true) {
			return
		}

		const position = this.normalizeInputPosition(event)
		const index = this.getKnobIndexAtPosition(position)

		// Selecting or adding new knob and selecting it
		if (index === -1) {
			this.props.onStartUpdating(this.props.name)

			this.selectedKnobIndex = this.addKnobAtPosition(position)

			this.props.onUpdate(this.props.name, this.points)
			this.props.onEndUpdating(this.props.name)
		} else {
			this.selectedKnobIndex = index
		}

		this.props.onKnobPositionUpdate(position)

		// Double tap to remove selected knob

		if (event.type === "dblclick") {
			event.preventDefault()
			this.removeSelectedKnobAndUpdate()
		}

		this.draw()
	}

	// Handles dragging selected knob
	protected onDrag = (event: React.PointerEvent<HTMLCanvasElement>) => {
		if (this.props.disabled === true) {
			return
		}

		const target = event.target as HTMLCanvasElement
		const position = this.normalizeInputPosition(event)
		const leewayX = 2 / this.safeRect.width

		if (event.type === "pointerdown") {
			this.isDragging = true
			target.setPointerCapture(event.pointerId)

			this.props.onStartUpdating(this.props.name)

			// On start, checking if we need to add a new dragging point

			const index = this.getKnobIndexAtPosition(position)

			if (index === -1) {
				this.selectedKnobIndex = this.addKnobAtPosition(position)
			} else {
				this.selectedKnobIndex = index
			}
		} else if (event.type === "pointermove") {
			// Now we know that we're dragging the selected knob index
			// We can't drag past neighbors' horizontal coordinates

			if (this.isDragging === false) {
				return
			}

			if (this.selectedKnobIndex === -1) {
				return
			}

			const selectedKnobPoint = this.points[this.selectedKnobIndex] as CurveAdjustmentPoint
			const leftmostIndex =
				this.points.filter((point) => {
					return point.x < selectedKnobPoint.x
				}).length - 1
			const leftmostPoint = this.points[leftmostIndex] as CurveAdjustmentPoint

			const rightmostIndex =
				this.points.length -
				this.points.filter((point) => {
					return point.x > selectedKnobPoint.x
				}).length

			const rightmostPoint = this.points[rightmostIndex] as CurveAdjustmentPoint
			const minX = this.selectedKnobIndex === 0 ? 0 : leftmostPoint.x + leewayX

			const maxX =
				this.selectedKnobIndex === this.points.length - 1 ? 1 : rightmostPoint.x - leewayX

			const constrainedPosition = position
			constrainedPosition.x = clamp(constrainedPosition.x, minX, maxX)

			this.points[this.selectedKnobIndex] = constrainedPosition

			this.props.onUpdate(this.props.name, this.points)

			this.props.onKnobPositionUpdate(position)

			this.draw()
		} else if (event.type === "pointerup") {
			this.isDragging = false
			target.releasePointerCapture(event.pointerId)

			this.props.onEndUpdating(this.props.name)
		}
	}

	// Normalizes input position to [0, 1]
	protected normalizeInputPosition = (
		event: React.MouseEvent<HTMLCanvasElement>,
	): CurveAdjustmentPoint => {
		//const deltaX = event.del
		//const deltaY = state.delta[1] / 2 // make vertical dragging less sensitive
		const localX = event.nativeEvent.offsetX //- this.boundingRect.left + deltaX
		const localY = event.nativeEvent.offsetY // - this.boundingRect.top + deltaY

		let { x, y } = {
			x: (localX - this.safeRect.x) / this.safeRect.width,
			y: 1.0 - (localY - this.safeRect.y) / this.safeRect.height,
		}

		x = clamp(x, 0, 1)
		y = clamp(y, 0, 1)

		return { x, y }
	}

	// Returns index of knob at touch position normalized coordinates or -1 if none
	protected getKnobIndexAtPosition = (position: CurveAdjustmentPoint): number => {
		const leewayX = this.drawingOptions.knobDraggingArea / this.safeRect.width
		// const leewayY = this.drawingOptions.knobDraggingArea / this.safeRect.height

		const queryRect: CurveAdjustmentRect = {
			x: position.x - leewayX,
			y: 0,
			width: leewayX * 2,
			height: 1.0,
		}

		return this.points.findIndex((point) => {
			return isPointInRect(queryRect, point)
		})
	}

	// Adds a knob at the specified touch position (sorted)
	protected addKnobAtPosition = (position: CurveAdjustmentPoint): number => {
		// Creating new knob index
		// Finding closest leftmost index at the x position
		const leftmostIndex = this.points.filter((point) => {
			return point.x < position.x
		}).length

		this.points.splice(leftmostIndex, 0, position)

		return leftmostIndex
	}

	// Removes selected knob
	protected removeSelectedKnob = () => {
		if (this.points.length <= 2) {
			return
		}

		if (this.selectedKnobIndex === -1) {
			return
		}

		this.points.splice(this.selectedKnobIndex, 1)
	}

	/* Drawing */

	// Draws the whole adjustment
	protected draw() {
		if (this.ctx instanceof CanvasRenderingContext2D === false) {
			return
		}

		// Keep drawing colors synced with current props so theme/channel switches are reflected.
		this.drawingOptions.knobColor = this.props.color
		this.drawingOptions.splineColor = this.props.color
		this.drawingOptions.gridColor = this.props.gridColor ?? "#333"

		this.ctx.clearRect(0, 0, this.size.width, this.size.height)
		this.ctx.beginPath()

		this.drawGrid()
		this.drawSpline()
		this.drawKnobs()
	}

	// Draws the grid with the calculated spacing
	protected drawGrid() {
		if (this.props.disabled === true) {
			return
		}

		const spacingX = this.safeRect.width / this.drawingOptions.gridCount
		const spacingY = this.safeRect.height / this.drawingOptions.gridCount

		this.ctx.save()
		this.ctx.strokeStyle = this.drawingOptions.gridColor
		this.ctx.lineWidth = this.drawingOptions.gridThickness

		// Draw vertical lines
		for (let x = this.safeRect.x; x <= this.safeRect.x + this.safeRect.width; x += spacingX) {
			this.ctx.beginPath()
			this.ctx.moveTo(x, this.safeRect.y)
			this.ctx.lineTo(x, this.safeRect.y + this.safeRect.height)
			this.ctx.stroke()
			this.ctx.closePath()
		}

		// Draw horizontal lines
		for (let y = this.safeRect.y; y <= this.safeRect.y + this.safeRect.height; y += spacingY) {
			this.ctx.beginPath()
			this.ctx.moveTo(this.safeRect.x, y)
			this.ctx.lineTo(this.safeRect.x + this.safeRect.width, y)
			this.ctx.stroke()
			this.ctx.closePath()
		}

		this.ctx.restore()
	}

	// Draws spline
	protected drawSpline() {
		if (this.points.length < 2) {
			return
		}

		/* Drawing current spline */

		// Drawing code adopted from here
		// It appears to be much faster than drawing spline with 2 pixel incremented line
		// https://github.com/dobarkod/canvas-bezier-multiple/blob/master/src/canvas-bezier-multipoint.js

		this.ctx.save()
		this.ctx.strokeStyle = this.drawingOptions.splineColor
		this.ctx.lineWidth = this.drawingOptions.splineThickness

		const points: vec2[] = []

		const px: number[] = []
		const py: number[] = []

		this.points.forEach((point) => {
			px.push(point.x)
			py.push(point.y)
		})

		// const spline = new renderer.spline.cubic(px, py)
		const spline = new Spline(px, py)

		for (let i = 0; i < this.points.length; i++) {
			const currentPoint = this.points[i] as CurveAdjustmentPoint
			const nextPoint = this.points[i + 1] as CurveAdjustmentPoint
			points.push([
				this.safeRect.x + currentPoint.x * this.safeRect.width,
				this.size.height - (this.safeRect.y + currentPoint.y * this.safeRect.height),
			])

			if (i === this.points.length - 1) {
				continue
			}

			const currentX = currentPoint.x
			const nextX = nextPoint.x
			const increment = (nextX - currentX) / 10

			for (let ix = currentX + increment; ix < nextX; ix += increment) {
				let iy = clamp(spline.at(ix), 0, 1)

				if (isNaN(iy) === true) {
					iy = currentPoint.y
				}

				points.push([
					this.safeRect.x + ix * this.safeRect.width,
					this.size.height - (this.safeRect.y + iy * this.safeRect.height),
				])
			}
		}

		const tension = 0.1

		this.ctx.beginPath()

		if (points.length === 2) {
			const first = points[0]
			const last = points[1]

			if (!first || !last) {
				return
			}

			// Left clamping
			if (first[0] !== this.safeRect.x) {
				this.ctx.moveTo(this.safeRect.x, first[1])
				this.ctx.lineTo(first[0], first[1])
			}

			this.ctx.moveTo(first[0], first[1])
			this.ctx.lineTo(last[0], last[1])

			// Right clamping
			if (last[0] !== this.safeRect.x + this.safeRect.width) {
				this.ctx.lineTo(this.safeRect.x + this.safeRect.width, last[1])
			}

			this.ctx.stroke()
			this.ctx.restore()

			return
		}

		// Helper function to calculate the hypotenuse
		function h(x: number, y: number) {
			return Math.sqrt(x * x + y * y)
		}

		type CPoint = { cp: vec2; cn: vec2 }
		const cpoints: Array<CPoint> = []
		points.forEach(() => {
			cpoints.push({ cp: [0, 0], cn: [0, 0] })
		})

		for (let i = 1; i < points.length - 1; i++) {
			const pi = points[i] // current point
			const pp = points[i - 1] // previous point
			const pn = points[i + 1] // next point

			if (!pi || !pp || !pn) {
				continue
			}

			const rdx = pn[0] - pp[0] // actual delta-x between previous and next points
			const rdy = pn[1] - pp[1] // actual delta-y between previous and next points
			const rd = h(rdx, rdy) // actual distance between previous and next points
			const dx = rdx / rd // normalized delta-x (so the total distance is 1)
			const dy = rdy / rd // normalized delta-y (so the total distance is 1)

			const dp = h(pi[0] - pp[0], pi[1] - pp[1]) // distance to previous point
			const dn = h(pi[0] - pn[0], pi[1] - pn[1]) // distance to next point

			const cpx = pi[0] - dx * dp * tension
			const cpy = pi[1] - dy * dp * tension
			const cnx = pi[0] + dx * dn * tension
			const cny = pi[1] + dy * dn * tension

			cpoints[i] = {
				cp: [cpx, cpy], // previous control point
				cn: [cnx, cny], // next control point
			}
		}

		const firstPoint = points[0] as [number, number]
		const lastPoint = points[points.length - 1] as [number, number]

		cpoints[0] = {
			cn: [
				(firstPoint[0] + (cpoints[1] as CPoint).cp[0]) / 2,
				(firstPoint[1] + (cpoints[1] as CPoint).cp[1]) / 2,
			],
			cp: [0, 0],
		}

		cpoints[points.length - 1] = {
			cp: [
				(lastPoint[0] + (cpoints[points.length - 2] as CPoint).cn[0]) / 2,
				(lastPoint[1] + (cpoints[points.length - 2] as CPoint).cn[1]) / 2,
			],
			cn: [0, 0],
		}

		/* Drawing */

		// Left clamping
		if (firstPoint[0] !== this.safeRect.x) {
			this.ctx.moveTo(this.safeRect.x, firstPoint[1])
			this.ctx.lineTo(firstPoint[0], firstPoint[1])
		}

		this.ctx.moveTo(firstPoint[0], firstPoint[1])

		for (let i = 1; i < points.length; i++) {
			const p = points[i]
			const cp = cpoints[i]
			const cpp = cpoints[i - 1]

			if (!p || !cp || !cpp) {
				continue
			}

			this.ctx.bezierCurveTo(cpp.cn[0], cpp.cn[1], cp.cp[0], cp.cp[1], p[0], p[1])
		}

		// Right clamping
		if (lastPoint[0] !== this.safeRect.x + this.safeRect.width) {
			this.ctx.lineTo(this.safeRect.x + this.safeRect.width, lastPoint[1])
		}

		this.ctx.stroke()
		this.ctx.restore()
	}

	// Draws all knobs
	protected drawKnobs() {
		this.points.forEach((normalizedPoint, index) => {
			const point: CurveAdjustmentPoint = {
				x: this.safeRect.x + normalizedPoint.x * this.safeRect.width,
				y: this.safeRect.y + normalizedPoint.y * this.safeRect.height,
			}

			// Drawing knob outline
			this.drawKnob(point, index === this.selectedKnobIndex)
		})
	}

	// Draws a single knob
	// Argument is not normalised and relative to canvas bounds size
	protected drawKnob(point: CurveAdjustmentPoint, isSelected: boolean) {
		this.ctx.save()

		this.ctx.strokeStyle = this.drawingOptions.knobColor
		this.ctx.lineWidth = this.drawingOptions.knobLineThickness

		this.ctx.beginPath()
		this.ctx.arc(
			point.x,
			this.size.height - point.y,
			this.drawingOptions.knobSize / 2.0,
			0,
			2 * Math.PI,
		)
		this.ctx.closePath()
		this.ctx.stroke()

		if (isSelected === true) {
			this.ctx.fillStyle = this.drawingOptions.knobColor
			this.ctx.fill()
		} else {
			this.ctx.globalCompositeOperation = "destination-out"
			this.ctx.fill()
		}

		this.ctx.restore()
	}
}
